import type { SeoResearch, TarefaDoRobo } from "../../../services/seo";
import { linkInvalido } from "./seo-produto-link";

/** Produto sendo preparado na tela Produtos (ainda não enviado ao robô). */
export type Rascunho = {
  id: string;
  nome: string;
  descricao: string;
  botao?: string;
  link?: string;
  image?: { name: string; contentType: string; dataUrl: string };
};

/** O backend publica no máximo 8 por envio (seo.max-items-per-run); o resto era cortado calado. */
export const MAX_POR_ENVIO = 8;
export const LIMITE_NOME = 58;
export const LIMITE_DESCRICAO = 1000;

const chave = (nome: string) => nome.trim().toLocaleLowerCase("pt-BR");

export const tarefaAberta = (t: TarefaDoRobo) => t.status === "PENDING" || t.status === "DISPATCHED";

/** "apagando" quando há tarefa de apagar aberta que pega este produto (pelo nome ou "todos"). */
export const situacaoNaFicha = (nome: string, tarefas: TarefaDoRobo[]): "apagando" | "na_ficha" =>
  tarefas.some(
    (t) => t.tipo === "product_delete" && tarefaAberta(t) && (t.todos || t.nomes.some((n) => chave(n) === chave(nome))),
  )
    ? "apagando"
    : "na_ficha";

/** Produtos enviados para publicar que o robô ainda não terminou (e que a leitura ainda não mostra). */
export const publicandoAgora = (tarefas: TarefaDoRobo[], naFicha: string[] | null): string[] => {
  const ja = new Set((naFicha ?? []).map(chave));
  const vistos = new Set<string>();
  return tarefas
    .filter((t) => t.tipo === "product" && tarefaAberta(t))
    .flatMap((t) => t.nomes)
    .filter((n) => {
      const k = chave(n);
      if (ja.has(k) || vistos.has(k)) return false;
      vistos.add(k);
      return true;
    });
};

/** Sugestões da pesquisa com IA (produtos + itens de uso "produto"), sem o que já está na ficha ou no rascunho. */
export const sugestoesDeProdutos = (
  pesquisa: SeoResearch | null,
  jaUsados: string[],
): Array<{ nome: string; descricao: string }> => {
  if (!pesquisa) return [];
  const usados = new Set(jaUsados.map(chave));
  const saida: Array<{ nome: string; descricao: string }> = [];
  const candidatos = [
    ...(pesquisa.produtos ?? []).map((p) => ({ nome: p.nome, descricao: p.descricao })),
    ...(pesquisa.itens ?? []).filter((i) => i.uso === "produto").map((i) => ({ nome: i.nome, descricao: i.descricao })),
  ];
  for (const c of candidatos) {
    const nome = (c.nome || "").trim().slice(0, LIMITE_NOME);
    if (!nome || usados.has(chave(nome))) continue;
    usados.add(chave(nome));
    saida.push({ nome, descricao: (c.descricao || "").slice(0, LIMITE_DESCRICAO) });
  }
  return saida;
};

/** Por que o botão Publicar está travado (null = pode publicar). */
export const motivoParaNaoPublicar = (rascunhos: Rascunho[]): string | null => {
  if (!rascunhos.length) return "Adicione ao menos um produto.";
  if (rascunhos.length > MAX_POR_ENVIO) return `Publique no máximo ${MAX_POR_ENVIO} produtos por vez: remova ${rascunhos.length - MAX_POR_ENVIO}.`;
  const semNome = rascunhos.filter((r) => !r.nome.trim()).length;
  if (semNome) return "Todo produto precisa de nome.";
  const semFoto = rascunhos.filter((r) => !r.image).length;
  if (semFoto) return `${semFoto} produto(s) sem foto: o Google exige foto em produto.`;
  if (rascunhos.some((r) => linkInvalido(r.link))) return "Há link inválido: use o endereço completo do site ou do WhatsApp.";
  const nomes = rascunhos.map((r) => chave(r.nome));
  if (new Set(nomes).size !== nomes.length) return "Há produtos com o mesmo nome.";
  return null;
};

/** Frase curta de uma tarefa do robô para o quadro de atividade. */
export const rotuloDaTarefa = (t: TarefaDoRobo): string => {
  const nomes = t.nomes.join(", ");
  const acao =
    t.tipo === "product"
      ? `${t.simulacao ? "Simular" : "Publicar"} "${nomes}"`
      : t.tipo === "product_delete"
        ? t.todos
          ? "Apagar todos os produtos"
          : `Apagar "${nomes}"`
        : "Ler os produtos da ficha";
  const hora = (iso: string) => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  };
  const estado =
    t.status === "DISPATCHED" && t.proximaTentativa
      ? `o Google demorou; nova tentativa às ${hora(t.proximaTentativa)}`
      : t.status === "PENDING"
        ? "na fila"
        : t.status === "DISPATCHED"
          ? "em andamento"
          : t.status === "DONE"
            ? "feito"
            : "não conseguiu";
  return `${acao} · ${estado}`;
};

const CHAVE_RASCUNHO = (perfil: string) => `gr_produtos_rascunho_${perfil}`;

/** Rascunhos ficam no navegador: fechar o editor não perde o que foi preparado. */
export const lerRascunhos = (perfil: string): Rascunho[] => {
  try {
    const bruto = localStorage.getItem(CHAVE_RASCUNHO(perfil));
    const lista = bruto ? (JSON.parse(bruto) as Rascunho[]) : [];
    return Array.isArray(lista) ? lista : [];
  } catch {
    return [];
  }
};

export const gravarRascunhos = (perfil: string, rascunhos: Rascunho[]) => {
  try {
    if (!rascunhos.length) localStorage.removeItem(CHAVE_RASCUNHO(perfil));
    else localStorage.setItem(CHAVE_RASCUNHO(perfil), JSON.stringify(rascunhos));
  } catch {
    // Fotos podem estourar o espaço do navegador: guarda sem elas.
    try {
      localStorage.setItem(CHAVE_RASCUNHO(perfil), JSON.stringify(rascunhos.map(({ image: _foto, ...resto }) => resto)));
    } catch {
      /* sem espaço: segue só na memória */
    }
  }
};
